import { settingsService } from './api';

// Same logic as api.js — resolves correct backend URL
const API_BASE_URL = import.meta.env.VITE_API_URL ||
  (['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:4009/api'
    : 'https://crmdemo.inplsoftwares.com/api');

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Fetch CRM booking financials via backend proxy.
 * Backend reads invoicingApiEndpoint + invoicingApiKey from app_settings.
 * No CORS issues for any role.
 */
async function fetchCrmBookingFinancials(fromDate, toDate, crmId = null) {
  const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
  if (crmId) params.append('crm_id', crmId);

  const response = await fetch(
    `${API_BASE_URL}/bookings/crm-financials?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  const data = await response.json();
  if (!response.ok || data.status === false) {
    console.warn('[Banquet API] Failed to fetch CRM booking financials:', data.message || 'External system error');
    // Return empty financials gracefully to prevent dashboard crash
    return { data: [] };
  }
  return data;
}

export const bookingService = {
  fetchCrmBookingFinancials,

  /**
   * Submit booking to external BMS via backend proxy.
   * Backend reads invoicingApiEndpoint + invoicingApiKey from app_settings.
   * No CORS issues for any role.
   */
  async submitToExternal(payload) {
    const response = await fetch(`${API_BASE_URL}/bookings/submit`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok || data.status === false) throw new Error(data.message || 'Failed to submit booking to banquet system');
    return data;
  },

  /**
   * Submit tentative booking to external BMS via backend proxy.
   */
  async submitTentativeToExternal(payload) {
    const response = await fetch(`${API_BASE_URL}/bookings/submit-tentative`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok || data.status === false) throw new Error(data.message || 'Failed to submit tentative booking to banquet system');
    return data;
  },

  /**
   * Fetch booking list via backend proxy.
   * Backend reads invoicingApiEndpoint + invoicingApiKey from app_settings.
   * No CORS issues for any role.
   *
   * @param {number} year
   * @param {number} month - 0-indexed (JS Date style)
   */
  async fetchBookingList(year, month) {
    const fromDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const toDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    const response = await fetch(
      `${API_BASE_URL}/bookings/banquet-list?from_date=${fromDate}&to_date=${toDate}`,
      { headers: getAuthHeaders() }
    );

    const data = await response.json();
    if (!data.status) {
      console.warn('[Banquet API] Failed to fetch banquet bookings:', data.message);
      return [];
    }

    // Normalize to CalendarView-compatible shape
    return (data.data || []).map((b) => ({
      id: `banquet-${b.id}`,
      banquetId: b.id,
      source: 'banquet',
      clientName: b.customer_name,
      eventDate: b.function_date,
      shift: b.booking_timing_name?.toLowerCase() === 'morning' ? 'Day' : 'Night',
      venue: b.lawn_name,
      functionName: b.function_name,
      noOfGuests: b.no_of_guest,
      orderNo: b.order_no,
      crmId: b.crm_id,
      mobileOne: b.mobile_one,
      mobileTwo: b.mobile_two,
      otherInstruction: b.other_instruction,
      shiftRemarks: b.shift_booking_remarks,
      setupName: b.setup_name,
      caterers: b.caterers_name,
    }));
  },

  /**
   * Fetch upcoming banquet bookings
   */
  async fetchUpcomingBookings(limit = 5) {
    const today = new Date();
    const fromDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // Fetch for next 6 months to ensure we get upcoming ones
    const future = new Date();
    future.setMonth(future.getMonth() + 6);
    const toDate = `${future.getFullYear()}-${String(future.getMonth() + 1).padStart(2, '0')}-${String(future.getDate()).padStart(2, '0')}`;

    const response = await fetch(
      `${API_BASE_URL}/bookings/banquet-list?from_date=${fromDate}&to_date=${toDate}`,
      { headers: getAuthHeaders() }
    );

    const data = await response.json();
    if (!data.status) {
      console.warn('[Banquet API] Failed to fetch upcoming bookings:', data.message);
      return []; // Return empty gracefully instead of throwing, so dashboard doesn't show errors
    }

    // Normalize, sort and limit
    const bookings = (data.data || []).map((b) => ({
      id: `banquet-${b.id}`,
      banquetId: b.id,
      source: 'banquet',
      clientName: b.customer_name,
      title: b.function_name || 'Event',
      startTime: b.function_date,
      eventDate: b.function_date,
      shift: b.booking_timing_name?.toLowerCase() === 'morning' ? 'Day' : 'Night',
      venue: b.lawn_name,
      functionName: b.function_name,
      noOfGuests: b.no_of_guest,
      orderNo: b.order_no,
      crmId: b.crm_id,
      mobileOne: b.mobile_one,
      mobileTwo: b.mobile_two,
      otherInstruction: b.other_instruction,
      shiftRemarks: b.shift_booking_remarks,
      setupName: b.setup_name,
      caterers: b.caterers_name,
    })).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    return bookings.slice(0, limit);
  }
};
